import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { App } from './App';
import { buildMsalConfig } from './auth/msalConfig';
import { loadRuntimeConfig } from './config/runtimeConfig';
import { RuntimeConfigContext } from './config/runtimeConfigContext';
import './hover.css';

async function bootstrap() {
  const cfg = await loadRuntimeConfig();
  const msalInstance = new PublicClientApplication(buildMsalConfig(cfg));
  await msalInstance.initialize();

  // Process any pending redirect response (login OR logout) BEFORE rendering. This is what
  // consumes the `#code=...&state=...` (login) or `?state=...` (logout) params MSAL appends to
  // the redirect URI and clears them from the address bar. Without it those params linger after
  // sign-in/sign-out and the auth state isn't fully reconciled.
  try {
    await msalInstance.handleRedirectPromise();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[msal] handleRedirectPromise failed:', err);
  }
  // Belt-and-suspenders: strip any residual auth params MSAL may have left on the URL so a
  // stale `state` (or code/session_state) never survives a redirect round-trip.
  stripAuthParamsFromUrl();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <RuntimeConfigContext.Provider value={cfg}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </RuntimeConfigContext.Provider>
    </React.StrictMode>,
  );
}

/**
 * Removes OAuth/OIDC redirect params (state, code, session_state, error, etc.) from the current
 * URL's query string and hash without triggering a navigation. Preserves any app-owned params.
 */
function stripAuthParamsFromUrl() {
  const AUTH_PARAMS = ['code', 'state', 'session_state', 'error', 'error_description', 'client_info', 'id_token', 'access_token'];
  try {
    const url = new URL(window.location.href);
    let changed = false;

    for (const p of AUTH_PARAMS) {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        changed = true;
      }
    }

    // MSAL can return the response in the fragment (#code=...&state=...); clear it if present.
    const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    if (hash && AUTH_PARAMS.some(p => new RegExp(`(^|&)${p}=`).test(hash))) {
      url.hash = '';
      changed = true;
    }

    if (changed) {
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* URL parsing failed; leave the address bar untouched. */
  }
}

bootstrap().catch((err) => {
  // eslint-disable-next-line no-console
  console.error('SPA bootstrap failed:', err);
  // The message can echo URL-derived text (e.g. an OAuth error_description), so it is set as text, never parsed as HTML.
  const message = document.createElement('pre');
  message.style.padding = '1rem';
  message.style.color = '#b00';
  message.textContent = `Failed to start the app: ${String(err)}`;
  document.body.replaceChildren(message);
});

